import { useState, useCallback, memo, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useGlobalContext } from '~/context/GlobalContext';
import emoticonsData from '~/data/emoticons.json';
import gifsData from '~/data/gifs.json';
import styles from './EmoticonContainer.module.css';

const ITEMS_PER_PAGE = 50;

// --- Komponent listy z leniwym ładowaniem ---
const PaginatedList = memo(({ items, type, onItemClick, active }: any) => {
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const loaderRef = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + ITEMS_PER_PAGE, items.length));
  }, [items.length]);

  useEffect(() => {
    if (!active) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [active, loadMore]);

  const visibleItems = items.slice(0, visibleCount);
  const basePath = type === 'em' ? '/img/emoticons/' : '/img/gifs/gifs_elements/';

  return (
    <div className={styles.group}>
      {visibleItems.map((item: any) => (
        <img
          key={item.id}
          src={`${basePath}${item.url}`}
          alt={item.name}
          className={type === 'em' ? styles.emoticon : styles.gif}
          loading="lazy"
          onClick={() => onItemClick(item.id, type)}
        />
      ))}
      {visibleCount < items.length && (
        <div ref={loaderRef} className={styles.loaderAnchor} style={{ height: '20px', width: '100%' }} />
      )}
    </div>
  );
});

// --- Główny komponent kontenera ---
const EmoticonContainer = () => {
  const { context, setContext } = useGlobalContext();
  const [activeTab, setActiveTab] = useState<'emoticons' | 'gifs'>('emoticons');
  const containerRef = useRef<HTMLDivElement>(null);

  // --- LOGIKA ZAMYKANIA: Kliknięcie poza i Scroll zewnętrzny ---
  useEffect(() => {
    const handleClose = (event: Event) => {
      const target = event.target as HTMLElement;

      // 1. Obsługa przewijania (scroll)
      if (event.type === 'scroll') {
        // Jeśli scrollujemy wewnątrz panelu emotikon - nic nie rób
        if (containerRef.current?.contains(target)) {
          return;
        }
        // Jeśli scrollujemy okno główne/czat - zamknij panel
        setContext(prev => ({ ...prev, isActiveEmoticonContainer: false }));
        return;
      }

      // 2. Obsługa kliknięcia (mousedown / touchstart)
      // Zamknij tylko jeśli kliknięto całkowicie poza obszarem containerRef
      if (containerRef.current && !containerRef.current.contains(target)) {
        setContext(prev => ({ ...prev, isActiveEmoticonContainer: false }));
      }
    };

    if (context.isActiveEmoticonContainer) {
      // setTimeout(0) zapobiega natychmiastowemu zamknięciu przy otwieraniu panelu
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClose);
        document.addEventListener('touchstart', handleClose);
        // capture: true pozwala wyłapać scrolla na dowolnym elemencie strony
        window.addEventListener('scroll', handleClose, { capture: true });
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClose);
        document.removeEventListener('touchstart', handleClose);
        window.removeEventListener('scroll', handleClose, { capture: true });
      };
    }
  }, [context.isActiveEmoticonContainer, setContext]);

  const handleItemClick = useCallback((id: any, type: any) => {
    setContext(prev => ({
      ...prev,
      media: ` <${type}/${id}> `,
      isActiveEmoticonContainer: false
    }));
  }, [setContext]);

  const containerStyle = {
    display: context.isActiveEmoticonContainer ? 'flex' : 'none'
  };

  return (
    <div
      ref={containerRef}
      data-component="EmoticonContainer"
      className={classNames(styles.emoticonContainer, styles.active)}
      style={containerStyle}
    >
      <div className={styles.tabs}>
        <div
          className={classNames(styles.tabButton, {
            [styles.activeTab]: activeTab === 'emoticons',
            [styles.emoticonButton]: true,
          })}
          onClick={(e) => {
            e.stopPropagation();
            setActiveTab('emoticons');
          }}
        >
          Emotikony
        </div>
        <div
          className={classNames(styles.tabButton, {
            [styles.activeTab]: activeTab === 'gifs'
          })}
          onClick={(e) => {
            e.stopPropagation();
            setActiveTab('gifs');
          }}
        >
          Gify
        </div>
      </div>

      <div className={styles.scrollableContent}>
        <div style={{ display: activeTab === 'emoticons' ? 'block' : 'none' }}>
          <PaginatedList
            items={emoticonsData}
            type="em"
            onItemClick={handleItemClick}
            active={activeTab === 'emoticons' && context.isActiveEmoticonContainer}
          />
        </div>

        <div style={{ display: activeTab === 'gifs' ? 'block' : 'none' }}>
          <PaginatedList
            items={Array.isArray(gifsData) ? gifsData : []}
            type="gif"
            onItemClick={handleItemClick}
            active={activeTab === 'gifs' && context.isActiveEmoticonContainer}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(EmoticonContainer);